import { el, note, type PanelDef, type ToolId } from '../panels.js';
import { canAddLayer, freshTextLayer } from '../state.js';
import { fillText } from '../texts.js';
import { phoneIconSvg, type PhoneIconName } from './icons.js';
import { openSheet, type SheetHandle } from './sheet.js';
import type { PhoneEnv, PhoneTabId } from './types-phone.js';

/**
 * The Add drawer: a bottom sheet with a scrollable tab strip, exactly the one
 * Zazzle's mobile web opens from the blue "+".
 *
 * The tabs do NOT reimplement the tool panels. Every tab but "Tekst" mounts
 * the very panel builder the desktop rail uses — the engine hands both shells
 * the same `PanelDef[]`, so a fix to the Slike panel lands on the phone the
 * same day. The "Tekst" tab is the one exception because Zazzle's mobile flow
 * is different from the desktop's: one primary button that turns the sheet
 * into "OTKAŽI | Dodaj tekst | PRIMENI" over a full-width textarea.
 */
interface TabDef {
  id: PhoneTabId;
  label: string;
  icon: PhoneIconName;
  /** The engine panel this tab shows. */
  tool: ToolId;
  /** Tried when the first one is missing or forbidden (older hosts). */
  alt?: ToolId;
}

/** Starting layouts and the artwork catalogue stay visible beside basic tools. */
export function drawerTabs(env: PhoneEnv): TabDef[] {
  const t = env.texts;
  const all: TabDef[] = [
    { id: 'templates', label: t.tabTemplates, icon: 'copy', tool: 'templates' },
    { id: 'images', label: t.tabImages, icon: 'image', tool: 'image' },
    { id: 'text', label: t.tabText, icon: 'text', tool: 'text' },
    { id: 'icons', label: t.tabIcons, icon: 'artwork', tool: 'icons', alt: 'artwork' },
    // Elementi is D's combined panel: dynamic shapes, every shape group, the
    // picture and vector libraries, and the QR dialog.
    { id: 'elements', label: t.tabElements, icon: 'shape', tool: 'elements', alt: 'shape' },
    { id: 'emoji', label: t.tabEmoji, icon: 'emoji', tool: 'emoji' },
    { id: 'background', label: t.tabBackground, icon: 'backdrop', tool: 'background' },
    { id: 'layers', label: t.tabLayers, icon: 'layers', tool: 'layers' },
    { id: 'sticker', label: t.tabSticker, icon: 'shape', tool: 'sticker' },
  ];
  // A tab is offered when the engine has a panel for it and the zone allows
  // it; Nalepnica disappears entirely on products that cannot be die-cut.
  return all.filter((tab) => Boolean(defFor(env, tab)?.allowed));
}

/** The def a tab mounts: its own panel, or the older alias when that is all there is. */
function defFor(env: PhoneEnv, tab: TabDef | undefined): PanelDef | undefined {
  if (!tab) return undefined;
  const own = env.defs.find((def) => def.id === tab.tool);
  if (own?.allowed) return own;
  const alt = tab.alt ? env.defs.find((def) => def.id === tab.alt) : undefined;
  return alt?.allowed ? alt : own;
}

/** The tool a tab shows, for the shell's `open(tool)` seam. */
export function tabForTool(tool: ToolId): PhoneTabId {
  switch (tool) {
    case 'image': return 'images';
    case 'text': return 'text';
    case 'elements':
    case 'shape':
    case 'qr': return 'elements';
    case 'icons':
    case 'artwork': return 'icons';
    case 'emoji': return 'emoji';
    case 'background': return 'background';
    case 'templates': return 'templates';
    case 'sticker': return 'sticker';
    default: return 'layers';
  }
}

export function openAddDrawer(env: PhoneEnv, wanted?: PhoneTabId): SheetHandle {
  const tabs = drawerTabs(env);
  let active: PhoneTabId = wanted && tabs.some((tab) => tab.id === wanted)
    ? wanted
    : tabs[0]?.id ?? 'layers';

  const sheet = openSheet({
    root: env.root,
    title: env.texts.addTitle,
    closeLabel: env.texts.close,
    tall: true,
    initialFocus: 'dialog',
    build: (body) => paint(body),
    onClose: () => env.refreshGrid(),
  });

  sheet.element.classList.add('is-catalog');
  const strip = el('div', 'pzd-ph-tabs');
  strip.setAttribute('role', 'tablist');
  strip.setAttribute('aria-label', env.texts.addTitle);
  sheet.strip.hidden = false;
  sheet.strip.append(strip);
  paintStrip();

  function paintStrip(): void {
    strip.textContent = '';
    for (const tab of tabs) {
      const button = el('button', `pzd-ph-tab is-${tab.id}${tab.id === active ? ' is-on' : ''}`);
      button.type = 'button';
      button.dataset.tab = tab.id;
      button.setAttribute('role', 'tab');
      button.setAttribute('aria-selected', String(tab.id === active));
      button.setAttribute('aria-label', tab.label);
      const icon = el('span');
      icon.innerHTML = phoneIconSvg(tab.icon, 20);
      button.append(icon, el('span', undefined, tab.label));
      button.addEventListener('click', () => {
        active = tab.id;
        paintStrip();
        sheet.setActions(null, null);
        sheet.setTitle(env.texts.addTitle);
        sheet.rebuild((nextBody) => paint(nextBody));
      });
      strip.append(button);
    }
  }

  /** Fills the sheet body with the active tab. */
  function paint(body: HTMLElement): void | (() => void) {
    const tab = tabs.find((item) => item.id === active);
    if (!tab) {
      body.append(note(env.texts.panelMissing));
      return;
    }
    if (tab.id === 'text') return paintTextTab(body);
    const def = defFor(env, tab);
    if (!def) {
      body.append(note(env.texts.panelMissing));
      return;
    }
    const result = def.render(body, env.ctx);
    return typeof result === 'function' ? result : undefined;
  }

  /** "Dodaj tekst" → the compose sheet, then back to the drawer's tabs. */
  function paintTextTab(body: HTMLElement): void {
    const button = el('button', 'pzd-ph-primary', env.texts.addTextButton);
    button.type = 'button';
    button.addEventListener('click', () => openCompose());
    body.append(button);
  }

  function openCompose(): void {
    sheet.setTitle(env.texts.titleAddText);
    sheet.strip.hidden = true;
    let value = '';
    sheet.setActions(
      { label: env.texts.cancel, quiet: true, onClick: () => backToTabs() },
      { label: env.texts.apply, onClick: () => commit(value) },
    );
    sheet.rebuild((body) => {
      const area = el('textarea', 'pzd-ph-area');
      area.placeholder = env.ctx.texts.textPlaceholder;
      area.setAttribute('aria-label', env.texts.titleAddText);
      area.maxLength = env.ctx.config.text.maxChars;
      area.addEventListener('input', () => {
        value = area.value;
      });
      body.append(area);
      window.setTimeout(() => area.focus(), 30);
    });
  }

  function backToTabs(): void {
    sheet.setTitle(env.texts.addTitle);
    sheet.strip.hidden = false;
    sheet.setActions(null, null);
    sheet.rebuild((body) => paint(body));
  }

  /** Adds the typed text as a layer, refusing politely when the zone is full. */
  function commit(value: string): void {
    const ctx = env.ctx;
    const text = value.trim();
    if (!text) {
      backToTabs();
      return;
    }
    const room = canAddLayer(ctx.store.state(), ctx.config, 'text');
    if (!room.ok) {
      ctx.toast(room.reason === 'kind'
        ? ctx.texts.toolNotAllowed
        : fillText(ctx.texts.maxLayers, { n: room.limit ?? ctx.config.maxLayers }), 'error');
      return;
    }
    const fontId = ctx.config.text.defaultFontId
      ?? ctx.config.text.fontIds[0]
      ?? ctx.fonts.list[0]?.id
      ?? '';
    void ctx.fonts.ensure(fontId).catch(() => undefined);
    ctx.dispatch({ type: 'add', layer: freshTextLayer(ctx.rawConfig, fontId, text, ctx.addCount()) });
    sheet.close();
  }

  return sheet;
}
